import React, { useRef, useState } from 'react';
import {
    Loader, Save, Plus, Trash2, Video, CheckCircle, AlertCircle,
    Home as HomeIcon, Wifi as WifiIcon, MessageSquare, ListChecks, X
} from 'lucide-react';
import { HouseManual, ManualItem } from '../types';
import { compressVideo, formatBytes } from '../utils/videoCompression';
import ManualBlockEditor, { parseBlocks, serializeBlocks } from './ManualBlockEditor';

export interface ManualEditorFormProps {
    manual: HouseManual;
    setManual: React.Dispatch<React.SetStateAction<HouseManual>>;
    onSave: () => Promise<void> | void;
    saving: boolean;
    uploadVideo: (blob: Blob, onProgress: (pct: number) => void) => Promise<string>;
    uploadImage: (file: File) => Promise<string>;
    onError: (msg: string) => void;
}

const inputClass = "w-full p-4 bg-white border border-gray-200 rounded-xl focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all text-gray-800";

const ManualEditorForm: React.FC<ManualEditorFormProps> = ({ manual, setManual, onSave, saving, uploadVideo, uploadImage, onError }) => {

    const updateField = <K extends keyof HouseManual>(key: K, value: HouseManual[K]) => {
        setManual(prev => ({ ...prev, [key]: value }));
    };

    const addItem = () => {
        setManual(prev => ({
            ...prev,
            items: [...prev.items, { title: '', instructions: '', videoUrl: '', displayOrder: prev.items.length }]
        }));
    };

    const updateItem = (idx: number, patch: Partial<ManualItem>) => {
        setManual(prev => ({
            ...prev,
            items: prev.items.map((it, i) => i === idx ? { ...it, ...patch } : it)
        }));
    };

    const removeItem = (idx: number) => {
        setManual(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
    };

    return (
        <div className="space-y-8">
            {/* SECTION: HOUSE */}
            <Section icon={<HomeIcon className="w-5 h-5 text-brand"/>} title="House">
                <Field label="House Name *">
                    <input type="text" value={manual.houseName} onChange={e => updateField('houseName', e.target.value)} className={inputClass} placeholder="e.g., Bandello"/>
                </Field>
                <Field label="Address">
                    <input type="text" value={manual.address} onChange={e => updateField('address', e.target.value)} className={inputClass} placeholder="e.g., Via Bandello 22, Milano"/>
                </Field>
                <Field label="Badge / Code">
                    <input type="text" value={manual.badge || ''} onChange={e => updateField('badge', e.target.value)} className={inputClass} placeholder="e.g., AH-001"/>
                </Field>
            </Section>

            {/* SECTION: WELCOME */}
            <Section icon={<MessageSquare className="w-5 h-5 text-brand"/>} title="Welcome & Assistance">
                <Field label="Welcome Message">
                    <textarea
                        value={manual.welcomeMessage || ''}
                        onChange={e => updateField('welcomeMessage', e.target.value)}
                        rows={3}
                        className={inputClass}
                        placeholder="A short welcome message for your guests..."
                    />
                </Field>
                <Field label="Assistance Phone">
                    <input type="tel" value={manual.assistancePhone || ''} onChange={e => updateField('assistancePhone', e.target.value)} className={inputClass} placeholder="+39 02 ..."/>
                </Field>
            </Section>

            {/* SECTION: CHECK-IN */}
            <Section icon={<HomeIcon className="w-5 h-5 text-brand"/>} title="Check-in">
                <Field label="Check-in Video">
                    <VideoUploader
                        currentUrl={manual.checkinVideoUrl}
                        onChange={url => updateField('checkinVideoUrl', url)}
                        uploadVideo={uploadVideo}
                        onError={onError}
                    />
                </Field>
                <div className="grid grid-cols-2 gap-4">
                    <Field label="Intercom Code">
                        <input type="text" value={manual.intercomCode || ''} onChange={e => updateField('intercomCode', e.target.value)} className={inputClass + ' font-mono tracking-widest text-center text-lg'} placeholder="080424"/>
                    </Field>
                    <Field label="Key Box Code">
                        <input type="text" value={manual.keyBoxCode || ''} onChange={e => updateField('keyBoxCode', e.target.value)} className={inputClass + ' font-mono tracking-widest text-center text-lg'} placeholder="2207"/>
                    </Field>
                </div>
            </Section>

            {/* SECTION: WIFI */}
            <Section icon={<WifiIcon className="w-5 h-5 text-brand"/>} title="Wi-Fi">
                <Field label="Network Name (SSID)">
                    <input type="text" value={manual.wifiName || ''} onChange={e => updateField('wifiName', e.target.value)} className={inputClass} placeholder="MyWiFi-5G"/>
                </Field>
                <Field label="Password">
                    <input type="text" value={manual.wifiPassword || ''} onChange={e => updateField('wifiPassword', e.target.value)} className={inputClass + ' font-mono'} placeholder="••••••••"/>
                </Field>
            </Section>

            {/* SECTION: ITEMS */}
            <Section icon={<ListChecks className="w-5 h-5 text-brand"/>} title="Instructions & Amenities">
                <div className="space-y-5">
                    {manual.items.map((item, idx) => (
                        <div key={idx} className="border border-gray-200 rounded-2xl p-5 bg-gray-50 relative">
                            <button onClick={() => removeItem(idx)} className="absolute top-3 right-3 text-gray-400 hover:text-red-500 p-1" title="Remove">
                                <X className="w-4 h-4"/>
                            </button>
                            <div className="space-y-4 mr-6">
                                <Field label={`Section #${idx + 1} — Title`}>
                                    <input type="text" value={item.title} onChange={e => updateItem(idx, { title: e.target.value })} className={inputClass} placeholder="e.g., Lavatrice, House Rules, Around the Area..."/>
                                </Field>
                                <Field label="Content Blocks">
                                    <ManualBlockEditor
                                        blocks={parseBlocks(item.content)}
                                        onChange={(blocks) => updateItem(idx, { content: serializeBlocks(blocks) })}
                                        uploadVideo={uploadVideo}
                                        uploadImage={uploadImage}
                                        onError={onError}
                                    />
                                </Field>
                            </div>
                        </div>
                    ))}

                    <button
                        onClick={addItem}
                        className="w-full py-6 border-2 border-dashed border-gray-300 rounded-2xl text-gray-400 hover:border-brand hover:text-brand hover:bg-brand/5 font-bold transition-all flex flex-col items-center gap-2"
                    >
                        <Plus className="w-6 h-6"/>
                        Add Amenity / Instruction
                    </button>
                </div>
            </Section>

            <div className="pt-4 flex justify-end">
                <button
                    onClick={() => onSave()}
                    disabled={saving}
                    className="bg-brand text-white px-10 py-4 rounded-xl font-bold hover:bg-[#c42e40] transition-all shadow-lg flex items-center gap-3 disabled:opacity-70"
                >
                    {saving ? <Loader className="w-5 h-5 animate-spin"/> : <Save className="w-5 h-5"/>}
                    Save Manual
                </button>
            </div>
        </div>
    );
};

const Section: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
    <section className="bg-white rounded-3xl border border-gray-200 shadow-sm p-6 md:p-8">
        <h2 className="font-serif font-bold text-xl text-primary mb-5 flex items-center gap-3">
            {icon} {title}
        </h2>
        <div className="space-y-4">{children}</div>
    </section>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div>
        <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">{label}</label>
        {children}
    </div>
);

// ============================================================
// VIDEO UPLOADER
// ============================================================

interface VideoUploaderProps {
    currentUrl?: string;
    onChange: (url: string) => void;
    uploadVideo: (blob: Blob, onProgress: (pct: number) => void) => Promise<string>;
    onError: (msg: string) => void;
}

const VideoUploader: React.FC<VideoUploaderProps> = ({ currentUrl, onChange, uploadVideo, onError }) => {
    const [phase, setPhase] = useState<'idle' | 'compressing' | 'uploading' | 'done'>('idle');
    const [progress, setProgress] = useState(0);
    const [originalSize, setOriginalSize] = useState<number>(0);
    const [compressedSize, setCompressedSize] = useState<number>(0);
    const [dragOver, setDragOver] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFiles = async (files: FileList | null) => {
        if (!files || !files[0]) return;
        const file = files[0];
        if (!file.type.startsWith('video/')) {
            onError('Please select a video file.');
            return;
        }
        setOriginalSize(file.size);
        try {
            setPhase('compressing');
            setProgress(0);
            const blob = await compressVideo(file, {
                onProgress: (pct) => setProgress(pct),
                onPhase: (p) => { if (p === 'loading') setPhase('compressing'); }
            });
            setCompressedSize(blob.size);

            setPhase('uploading');
            setProgress(0);
            const url = await uploadVideo(blob, (pct) => setProgress(pct));
            if (url) {
                onChange(url);
                setPhase('done');
                setTimeout(() => setPhase('idle'), 1500);
            } else {
                onError('Upload failed.');
                setPhase('idle');
            }
        } catch (err: any) {
            onError(err?.message || 'Video processing failed.');
            setPhase('idle');
        }
    };

    if (currentUrl && phase === 'idle') {
        return (
            <div className="space-y-3">
                <div className="relative rounded-xl overflow-hidden bg-black">
                    <video src={currentUrl} controls playsInline className="w-full max-h-72 object-contain"/>
                </div>
                <button
                    onClick={() => onChange('')}
                    className="text-xs text-red-500 hover:text-red-700 font-bold flex items-center gap-1"
                >
                    <Trash2 className="w-3 h-3"/> Remove video
                </button>
            </div>
        );
    }

    if (phase === 'compressing' || phase === 'uploading') {
        return (
            <div className="p-6 bg-brand/5 border-2 border-brand/30 rounded-2xl text-center">
                <Loader className="w-6 h-6 animate-spin text-brand mx-auto mb-3"/>
                <p className="font-bold text-sm text-gray-800">
                    {phase === 'compressing' ? 'Compressing video…' : 'Uploading…'}
                </p>
                <div className="w-full bg-white rounded-full h-2 mt-3 overflow-hidden">
                    <div className="bg-brand h-full transition-all duration-200" style={{ width: `${progress}%` }}/>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                    {progress}%
                    {phase === 'uploading' && compressedSize > 0 && ` — ${formatBytes(compressedSize)}`}
                </p>
                {phase === 'compressing' && originalSize > 0 && (
                    <p className="text-[10px] text-gray-400 mt-1">Original: {formatBytes(originalSize)}</p>
                )}
            </div>
        );
    }

    if (phase === 'done') {
        return (
            <div className="p-6 bg-green-50 border-2 border-green-200 rounded-2xl text-center">
                <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2"/>
                <p className="font-bold text-sm text-green-700">
                    Video uploaded
                    {compressedSize > 0 && originalSize > 0 && (
                        <span className="block text-xs font-normal text-green-600 mt-1">
                            {formatBytes(originalSize)} → {formatBytes(compressedSize)} (saved {Math.round((1 - compressedSize / originalSize) * 100)}%)
                        </span>
                    )}
                </p>
            </div>
        );
    }

    return (
        <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
            onClick={() => inputRef.current?.click()}
            className={`p-6 border-2 border-dashed rounded-2xl text-center cursor-pointer transition-all ${dragOver ? 'border-brand bg-brand/5' : 'border-gray-300 hover:border-brand hover:bg-brand/5'}`}
        >
            <Video className={`w-8 h-8 mx-auto mb-2 ${dragOver ? 'text-brand' : 'text-gray-400'}`}/>
            <p className="font-bold text-sm text-gray-700">Tap or drop a video here</p>
            <p className="text-xs text-gray-400 mt-1">It will be compressed automatically before upload.</p>
            <input
                ref={inputRef}
                type="file"
                accept="video/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
            />
        </div>
    );
};

export default ManualEditorForm;
